import { useState, useCallback } from 'react';
import { Check, FlaskConical, BookOpen, Award } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import SolubilityGrid from './SolubilityGrid';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens?: string; observe?: string[]; equation?: string }> = {
  1: {
    title: 'Analyzing Solubility Patterns',
    content: 'With 88 combinations tested, we can now identify patterns. Some anions (like Cl-, Br-, I-) form precipitates with only specific cations (Ag+), while others (like CO3 2-, PO4 3-) form precipitates with most cations.',
    whatHappens: 'You examine the complete grid to identify which combinations produced precipitates and which remained soluble.',
    observe: ['Most halides are soluble (except with Ag+)', 'Most carbonates/phosphates are insoluble', 'Sulfates are mostly soluble (except Ba2+, Ca2+)', 'Hydroxides are mostly insoluble (except Group 1, Ba2+)']
  },
  2: {
    title: 'Deriving Solubility Rules',
    content: 'From experimental observations, we can derive general rules. These "solubility rules" are empirical generalizations based on Ksp values - smaller Ksp means less soluble.',
    whatHappens: 'Using the pattern of precipitates, you formulate rules that predict solubility for any cation-anion combination.',
    observe: ['Group 1 salts are always soluble', 'Nitrates are always soluble', 'Halides are soluble (except Ag+, Pb2+, Hg2 2+)', 'Most hydroxides/carbonates/phosphates are insoluble'],
    equation: 'Ksp = [cation]^m [anion]^n (solubility product)'
  },
  3: {
    title: 'Comparing with Reference Rules',
    content: 'Standard solubility rules have been established from extensive testing. Your experimental observations should match these rules, demonstrating how empirical data leads to scientific generalizations.',
    whatHappens: 'You compare your derived rules with the established solubility rules to verify your conclusions.',
    observe: ['Rules predict which salts precipitate', 'Exceptions are noted for each rule', 'Rules help predict double displacement outcomes', 'Ksp values quantify solubility']
  }
};

const SOLUBILITY_RULES = [
  {
    id: 1,
    rule: 'All Na+, K+, and NH4+ salts are soluble',
    explanation: 'Group 1 cations and ammonium form soluble salts with all anions'
  },
  {
    id: 2,
    rule: 'All nitrates (NO3-) are soluble',
    explanation: 'Nitrate salts have no common insoluble forms'
  },
  {
    id: 3,
    rule: 'Most chlorides, bromides, and iodides are soluble',
    explanation: 'Exceptions: Ag+, Pb2+, Hg22+ form insoluble halides'
  },
  {
    id: 4,
    rule: 'Most sulfates are soluble',
    explanation: 'Exceptions: BaSO₄, PbSO₄, CaSO₄ (slightly soluble)'
  },
  {
    id: 5,
    rule: 'Most hydroxides are insoluble',
    explanation: 'Exceptions: Group 1 hydroxides and Ba(OH)2 are soluble'
  },
  {
    id: 6,
    rule: 'Most carbonates and phosphates are insoluble',
    explanation: 'Exceptions: Group 1 and NH4+ carbonates/phosphates are soluble'
  },
  {
    id: 7,
    rule: 'Most sulfides are insoluble',
    explanation: 'Exceptions: Group 1 and NH4+ sulfides; Group 2 sulfides are slightly soluble'
  }
];

export default function Part9Analysis() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [patternsAnalyzed, setPatternsAnalyzed] = useState(false);
  const [rulesDerived, setRulesDerived] = useState(false);
  const [comparisonComplete, setComparisonComplete] = useState(false);
  const [selectedRule, setSelectedRule] = useState<number | null>(null);
  const [currentStep, setCurrentStep] = useState(1);

  const completedSteps = [patternsAnalyzed, rulesDerived, comparisonComplete].filter(Boolean).length;

  const handleAnalyzePatterns = useCallback(() => {
    if (!patternsAnalyzed) {
      setPatternsAnalyzed(true);
      setCurrentStep(2);
    }
  }, [patternsAnalyzed]);

  const handleDeriveRules = useCallback(() => {
    if (patternsAnalyzed && !rulesDerived) {
      setRulesDerived(true);
      setCurrentStep(3);
    }
  }, [patternsAnalyzed, rulesDerived]);

  const handleCompareRules = useCallback(() => {
    if (rulesDerived && !comparisonComplete) {
      setComparisonComplete(true);
    }
  }, [rulesDerived, comparisonComplete]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part IX: Results Analysis</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Analyze Patterns', hint: 'Review your complete grid', done: patternsAnalyzed },
            { id: 2, title: 'Derive Rules', hint: 'State solubility rules', done: rulesDerived },
            { id: 3, title: 'Compare with Reference', hint: 'Verify your observations', done: comparisonComplete }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Rule Selection */}
        {rulesDerived && (
          <div className="rules-panel">
            <h4>Solubility Rules</h4>
            <div className="rules-list">
              {SOLUBILITY_RULES.map(rule => (
                <div
                  key={rule.id}
                  className={`rule-item ${selectedRule === rule.id ? 'selected' : ''}`}
                  onClick={() => setSelectedRule(rule.id)}
                >
                  <span className="rule-number">{rule.id}</span>
                  <span className="rule-text">{rule.rule}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Solubility Analysis"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Completed grid', 'Reference table', 'Lab notebook']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="analysis-workspace">
          {/* Step 1: View Complete Grid */}
          {currentStep === 1 && !patternsAnalyzed && (
            <div className="analysis-section">
              <h3><BookOpen size={24} /> Review Your Complete Grid</h3>
              <p>All 88 combinations have been tested. Look for patterns in precipitate formation.</p>
              <SolubilityGrid showAll={true} />
              <button className="action-btn" onClick={handleAnalyzePatterns}>
                I Have Analyzed the Patterns
              </button>
            </div>
          )}

          {/* Step 2: Derive Rules */}
          {currentStep === 2 && patternsAnalyzed && !rulesDerived && (
            <div className="analysis-section">
              <h3>Derive Solubility Rules</h3>
              <p>Based on your observations, answer these questions:</p>
              <div className="questions-list">
                <div className="question">
                  <span className="q-num">Q1:</span>
                  <span>Which anions formed precipitates with almost ALL cations?</span>
                  <span className="hint">(CO32-, PO43-, OH-, S2-)</span>
                </div>
                <div className="question">
                  <span className="q-num">Q2:</span>
                  <span>Which anions formed precipitates with only SPECIFIC cations?</span>
                  <span className="hint">(Cl-, Br-, I- with Ag+; SO42- with Ba2+, Ca2+)</span>
                </div>
                <div className="question">
                  <span className="q-num">Q3:</span>
                  <span>What do the insoluble halides have in common?</span>
                  <span className="hint">(All form with Ag+)</span>
                </div>
              </div>
              <button className="action-btn" onClick={handleDeriveRules}>
                View Standard Solubility Rules
              </button>
            </div>
          )}

          {/* Step 3: Compare with Reference */}
          {currentStep === 3 && rulesDerived && !comparisonComplete && (
            <div className="analysis-section">
              <h3>Compare with Standard Rules</h3>
              <p>Click each rule to see how it applies to your experimental results.</p>

              {selectedRule && (
                <div className="rule-detail">
                  <h4>Rule {selectedRule}</h4>
                  <p className="rule-statement">{SOLUBILITY_RULES.find(r => r.id === selectedRule)?.rule}</p>
                  <p className="rule-explanation">{SOLUBILITY_RULES.find(r => r.id === selectedRule)?.explanation}</p>
                </div>
              )}

              <SolubilityGrid showAll={true} />

              <button className="action-btn" onClick={handleCompareRules}>
                Complete Analysis
              </button>
            </div>
          )}

          {/* Completion */}
          {comparisonComplete && (
            <div className="completion-section">
              <Award size={64} className="award-icon" />
              <h2>Experiment Complete!</h2>
              <p>You have successfully:</p>
              <ul className="achievements">
                <li><Check size={16} /> Tested 88 cation-anion combinations</li>
                <li><Check size={16} /> Observed precipitate formation patterns</li>
                <li><Check size={16} /> Derived solubility rules from experimental data</li>
                <li><Check size={16} /> Compared results with standard reference</li>
              </ul>

              <div className="summary-rules">
                <h4>Key Solubility Rules to Remember</h4>
                <div className="rule-cards">
                  <div className="rule-card soluble">
                    <h5>Generally SOLUBLE</h5>
                    <ul>
                      <li>All Group 1 (Na+, K+) salts</li>
                      <li>All nitrates (NO3-)</li>
                      <li>Most halides (Cl-, Br-, I-)</li>
                      <li>Most sulfates (SO42-)</li>
                    </ul>
                  </div>
                  <div className="rule-card insoluble">
                    <h5>Generally INSOLUBLE</h5>
                    <ul>
                      <li>Most hydroxides (OH-)</li>
                      <li>Most carbonates (CO32-)</li>
                      <li>Most phosphates (PO43-)</li>
                      <li>Most sulfides (S2-)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <style>{`
          .analysis-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 800px;
          }

          .analysis-section {
            width: 100%;
            text-align: center;
          }

          .analysis-section h3 {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            margin: 0 0 12px 0;
          }

          .analysis-section > p {
            color: var(--text-secondary);
            margin: 0 0 20px 0;
          }

          .questions-list {
            text-align: left;
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 20px;
          }

          .question {
            display: flex;
            flex-direction: column;
            gap: 4px;
            margin-bottom: 16px;
            padding-bottom: 16px;
            border-bottom: 1px solid var(--border);
          }

          .question:last-child {
            margin-bottom: 0;
            padding-bottom: 0;
            border-bottom: none;
          }

          .q-num {
            font-weight: 700;
            color: var(--secondary);
          }

          .hint {
            font-size: 12px;
            color: var(--text-secondary);
            font-style: italic;
          }

          .action-btn {
            background: var(--secondary);
            color: white;
            border: none;
            padding: 12px 28px;
            border-radius: 8px;
            font-weight: 600;
            font-size: 15px;
            cursor: pointer;
            margin-top: 20px;
            transition: all 0.2s;
          }

          .action-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 168, 107, 0.3);
          }

          .rules-panel {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .rules-panel h4 {
            margin: 0 0 10px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .rules-list {
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .rule-item {
            display: flex;
            gap: 8px;
            padding: 8px;
            background: var(--bg-surface);
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s;
            font-size: 11px;
          }

          .rule-item:hover, .rule-item.selected {
            background: rgba(0, 168, 107, 0.1);
            border-left: 3px solid var(--secondary);
          }

          .rule-number {
            font-weight: 700;
            color: var(--secondary);
            min-width: 20px;
          }

          .rule-text {
            color: var(--text-secondary);
          }

          .rule-detail {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 20px;
            border-left: 4px solid var(--secondary);
            text-align: left;
          }

          .rule-detail h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
          }

          .rule-statement {
            font-weight: 600;
            margin: 0 0 8px 0;
          }

          .rule-explanation {
            color: var(--text-secondary);
            font-size: 14px;
            margin: 0;
          }

          .completion-section {
            text-align: center;
            width: 100%;
          }

          .award-icon {
            color: #F59E0B;
            margin-bottom: 16px;
          }

          .completion-section h2 {
            margin: 0 0 12px 0;
            color: var(--secondary);
          }

          .achievements {
            list-style: none;
            padding: 0;
            margin: 20px 0;
            text-align: left;
            max-width: 400px;
            margin-left: auto;
            margin-right: auto;
          }

          .achievements li {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 0;
            color: var(--text-secondary);
          }

          .achievements li svg {
            color: var(--secondary);
          }

          .summary-rules {
            background: var(--bg-elevated);
            border-radius: 12px;
            padding: 20px;
            margin-top: 20px;
          }

          .summary-rules h4 {
            margin: 0 0 16px 0;
            color: var(--text-primary);
          }

          .rule-cards {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          }

          .rule-card {
            padding: 16px;
            border-radius: 8px;
            text-align: left;
          }

          .rule-card.soluble {
            background: rgba(0, 168, 107, 0.1);
            border: 1px solid var(--secondary);
          }

          .rule-card.insoluble {
            background: rgba(245, 158, 11, 0.1);
            border: 1px solid #F59E0B;
          }

          .rule-card h5 {
            margin: 0 0 10px 0;
            font-size: 13px;
          }

          .rule-card.soluble h5 {
            color: var(--secondary);
          }

          .rule-card.insoluble h5 {
            color: #F59E0B;
          }

          .rule-card ul {
            margin: 0;
            padding-left: 20px;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .rule-card li {
            margin: 4px 0;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp7"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
